import atlas from "../public/sprites/jolie/jolie_spritesheet.json";
import { jolieConfig as config } from "./config/jolie";
import { interactionConfig } from "./config/ui";
import { JolieBehavior, selectJolieFrame } from "./jolie/behavior";
import type { PointerPosition } from "./jolie/behavior";
import { JolieEntrance } from "./jolie/entrance";

function startJolie() {
	const canvas = document.querySelector<HTMLCanvasElement>("#jolie");
	if (!canvas) return;
	const context = canvas.getContext("2d");
	const layer = canvas.parentElement;
	if (!context || !layer) return;
	const ctx = context;
	const surface = canvas;
	const reduced = matchMedia(interactionConfig.reducedMotionQuery);
	const image = new Image();
	const size = atlas.frames.idle_0.sourceSize;
	const anchorX = size.w * atlas.meta.anchor.x;
	const anchorY = size.h * atlas.meta.anchor.y;
	const touchScratchDurationMs = atlas.animations.scratch.frames
		.slice(3, 6)
		.reduce(
			(total, name) =>
				total +
				atlas.frames[name as keyof typeof atlas.frames].duration,
			0,
		);
	surface.style.width = `${size.w * config.displayScale}px`;
	surface.style.height = `${size.h * config.displayScale}px`;
	surface.style.bottom = `${-(size.h - anchorY) * config.displayScale}px`;
	let viewport = { width: window.innerWidth, ground: window.innerHeight };
	const behavior = new JolieBehavior(viewport.width);
	const entrance = new JolieEntrance(atlas);
	let pointer: PointerPosition = null;
	let loaded = false;
	let frameId = 0;
	let lastTime: number | undefined;
	let lastSprite = "";
	let lastFlip = false;

	function resize() {
		const bounds = layer!.getBoundingClientRect();
		viewport = { width: bounds.width, ground: bounds.bottom };
		const resolution =
			config.displayScale * Math.min(window.devicePixelRatio || 1, 2);
		surface.width = Math.round(size.w * resolution);
		surface.height = Math.round(size.h * resolution);
		ctx.setTransform(
			surface.width / size.w,
			0,
			0,
			surface.height / size.h,
			0,
			0,
		);
		ctx.imageSmoothingEnabled = false;
		lastSprite = "";
		wake();
	}

	function wake() {
		if (loaded && !document.hidden && !frameId)
			frameId = requestAnimationFrame(render);
	}

	function render(now: number) {
		frameId = 0;
		if (document.hidden) return;
		if (window.pixelIntro?.active) {
			lastTime = undefined;
			wake();
			return;
		}
		const dt = lastTime === undefined ? 0 : now - lastTime;
		lastTime = now;
		if (entrance.active) {
			entrance.update(dt, viewport, reduced.matches);
			behavior.x = entrance.x;
			behavior.facing = "left";
			if (!entrance.active) {
				behavior.finishEntrance();
				behavior.update(0, pointer, viewport, reduced.matches);
			}
		} else behavior.update(dt, pointer, viewport, reduced.matches);
		const name = (
			entrance.active
				? entrance.frameName
				: selectJolieFrame(atlas, behavior, reduced.matches)
		) as keyof typeof atlas.frames;
		const flip = !entrance.active && behavior.facing === "left";
		surface.hidden = false;
		surface.style.left = `${behavior.x - anchorX * config.displayScale}px`;
		surface.style.bottom = `${-(size.h - anchorY) * config.displayScale + entrance.height}px`;
		surface.dataset.mode = entrance.active ? entrance.phase : behavior.mode;
		surface.dataset.facing = behavior.facing;
		surface.dataset.frame = name;
		if (name !== lastSprite || flip !== lastFlip) {
			const sprite = atlas.frames[name];
			const rect = sprite.frame;
			const offset = sprite.spriteSourceSize;
			ctx.clearRect(0, 0, size.w, size.h);
			ctx.save();
			ctx.translate(anchorX, 0);
			ctx.scale(flip ? -1 : 1, 1);
			ctx.drawImage(
				image,
				rect.x,
				rect.y,
				rect.w,
				rect.h,
				offset.x - anchorX,
				offset.y,
				offset.w,
				offset.h,
			);
			ctx.restore();
			lastSprite = name;
			lastFlip = flip;
		}
		if (!reduced.matches || behavior.isTouchScratching) wake();
	}

	function clearPointer() {
		pointer = null;
		wake();
	}
	window.addEventListener(
		"pointerdown",
		(event) => {
			if (
				event.pointerType !== "touch" ||
				!loaded ||
				entrance.active ||
				window.pixelIntro?.active
			)
				return;
			pointer = null;
			if (
				behavior.touch(
					{ x: event.clientX, y: event.clientY },
					viewport,
					touchScratchDurationMs,
				)
			) {
				lastTime = undefined;
			}
			wake();
		},
		{ passive: true },
	);
	window.addEventListener(
		"pointermove",
		(event) => {
			if (event.pointerType === "touch") {
				clearPointer();
				return;
			}
			pointer = { x: event.clientX, y: event.clientY };
			wake();
		},
		{ passive: true },
	);
	window.addEventListener("pointerout", (event) => {
		if (!event.relatedTarget) clearPointer();
	});
	window.addEventListener("blur", clearPointer);
	window.addEventListener("pointercancel", clearPointer);
	window.addEventListener("resize", resize);
	window.visualViewport?.addEventListener("resize", resize);
	document.addEventListener("visibilitychange", () => {
		lastTime = undefined;
		clearPointer();
		if (document.hidden) {
			cancelAnimationFrame(frameId);
			frameId = 0;
		}
	});
	reduced.addEventListener("change", () => {
		lastTime = undefined;
		wake();
	});
	image.onload = () => {
		loaded = true;
		resize();
	};
	// This decorative sprite must not affect the rest of the page if unavailable.
	image.onerror = () => surface.remove();
	image.src = `sprites/jolie/${atlas.meta.image}`;
}

startJolie();
